import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Wallet, 
  Building2, 
  ArrowDownToLine,
  Download,
  TrendingUp,
  TrendingDown,
  Percent,
  Receipt,
  History,
  User,
  Phone,
  CreditCard,
  FileText,
  AlertCircle,
  CheckCircle2,
  Trash2,
  Users,
  Loader2,
  ListChecks,
  ArrowRight,
  MessageCircle,
  Mail,
  FileDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { propertiesAPI, withdrawalsAPI } from '../lib/api';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';

const OwnerBalance = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [balanceData, setBalanceData] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  
  // Withdrawal modal state
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawalForm, setWithdrawalForm] = useState({
    amount: '',
    payment_method: 'especes',
    beneficiary_name: '',
    beneficiary_phone: '',
    notes: ''
  });
  
  // PDF download state
  const [downloadingPdf, setDownloadingPdf] = useState(null);

  // Global balance state
  const [showGlobalModal, setShowGlobalModal] = useState(false);
  const [loadingGlobal, setLoadingGlobal] = useState(false);
  const [globalData, setGlobalData] = useState(null);
  const [downloadingGlobalPdf, setDownloadingGlobalPdf] = useState(false);

  // Get user role - memoized to avoid re-parsing on every render
  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user.role);

  const fetchProperties = useCallback(async () => {
    try {
      const response = await propertiesAPI.getAll({ transaction_type: 'location' });
      setProperties(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des biens');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const fetchBalance = async () => {
    if (!selectedProperty) {
      toast.error('Veuillez sélectionner un bien');
      return;
    }

    setLoadingBalance(true);
    try {
      const response = await propertiesAPI.getOwnerBalance(selectedProperty);
      setBalanceData(response.data);
      
      // Pre-fill beneficiary from owner info
      if (response.data.owner_name) {
        setWithdrawalForm(prev => ({
          ...prev,
          beneficiary_name: response.data.owner_name,
          beneficiary_phone: response.data.owner_phone || ''
        }));
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors du chargement du bilan';
      toast.error(message);
      setBalanceData(null);
    } finally {
      setLoadingBalance(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawalForm.amount || parseFloat(withdrawalForm.amount) <= 0) {
      toast.error('Veuillez entrer un montant valide');
      return;
    }
    
    if (!withdrawalForm.beneficiary_name) {
      toast.error('Veuillez entrer le nom du bénéficiaire');
      return;
    }

    const amount = parseFloat(withdrawalForm.amount);
    
    // Avertissement en cas de découvert mais on laisse passer
    if (amount > balanceData.available_balance) {
      const newBalance = balanceData.available_balance - amount;
      if (!window.confirm(`Attention: ce retrait mettra le compte en découvert (${formatCurrency(newBalance, 'XOF')}). Continuer ?`)) {
        return;
      }
    }

    setWithdrawing(true);
    try {
      const response = await propertiesAPI.createWithdrawal(selectedProperty, {
        property_id: selectedProperty,
        amount: amount,
        currency: 'XOF',
        payment_method: withdrawalForm.payment_method,
        beneficiary_name: withdrawalForm.beneficiary_name,
        beneficiary_phone: withdrawalForm.beneficiary_phone || null,
        notes: withdrawalForm.notes || null
      });
      
      toast.success('Retrait enregistré avec succès');
      setShowWithdrawModal(false);
      
      // Reset form
      setWithdrawalForm({
        amount: '',
        payment_method: 'especes',
        beneficiary_name: balanceData?.owner_name || '',
        beneficiary_phone: balanceData?.owner_phone || '',
        notes: ''
      });
      
      // Refresh balance
      fetchBalance();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors du retrait';
      toast.error(message);
    } finally {
      setWithdrawing(false);
    }
  };

  const handleDownloadReceipt = async (withdrawalId, reference) => {
    setDownloadingPdf(withdrawalId);
    try {
      const response = await withdrawalsAPI.downloadReceipt(withdrawalId);
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attestation_retrait_${reference}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Attestation téléchargée');
    } catch (error) {
      let message = 'Erreur lors du téléchargement';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          message = errorData.detail || message;
        } catch (parseError) {
          console.error('Error parsing blob response:', parseError);
          // Unable to parse blob response
        }
      }
      toast.error(message);
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleDeleteWithdrawal = async (withdrawalId, reference) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le retrait ${reference} ?`)) {
      return;
    }
    
    try {
      await withdrawalsAPI.delete(withdrawalId);
      toast.success('Retrait supprimé avec succès');
      // Refresh balance data
      if (selectedProperty) {
        fetchBalance();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const openGlobalBalance = async () => {
    setShowGlobalModal(true);
    setLoadingGlobal(true);
    setGlobalData(null);
    try {
      const res = await api.get('/owners/global-balance');
      setGlobalData(res.data);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors du chargement du relevé global';
      toast.error(message);
    } finally {
      setLoadingGlobal(false);
    }
  };

  const handleDownloadGlobalPdf = async () => {
    setDownloadingGlobalPdf(true);
    try {
      const res = await api.get('/owners/global-balance/pdf', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.download = `releve_global_proprietaires_${stamp}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Relevé global téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloadingGlobalPdf(false);
    }
  };

  const handleSelectFromGlobal = (propertyId) => {
    setSelectedProperty(propertyId);
    setShowGlobalModal(false);
    // Defer to next tick so state is committed before fetching
    setTimeout(() => {
      const ev = new Event('open');
      window.dispatchEvent(ev);
    }, 0);
  };

  const handleDownloadOwnerPdf = async (owner) => {
    try {
      const params = new URLSearchParams({ owner_name: owner.owner_name });
      if (owner.owner_phone) params.append('owner_phone', owner.owner_phone);
      const res = await api.get(`/owners/balance-by-name/pdf?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safe = owner.owner_name.replace(/[^a-z0-9_-]/gi, '_');
      link.download = `bilan_${safe}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Bilan de ${owner.owner_name} telecharge`);
    } catch (error) {
      toast.error('Erreur lors du telechargement');
    }
  };

  const buildOwnerMessage = (owner) => {
    const f = (v) => formatCurrency(v, 'XOF');
    const props = owner.properties.map(p => `• ${p.property_title} (${p.property_code}) : ${f(p.available_balance)}`).join('\n');
    return (
`Bonjour ${owner.owner_name},

Voici votre bilan financier consolide :

- Loyers encaisses : ${f(owner.total_received)}
- Commission agence : ${f(owner.total_commission)}
- Net du : ${f(owner.net_for_owner)}
- Deja retire : ${f(owner.total_withdrawn)}

SOLDE DISPONIBLE : ${f(owner.available_balance)}

Detail par bien :
${props}

Le PDF detaille est joint a ce message.

Cordialement,
${user?.name || "L'equipe GAB'S SERVICES HABITAT"}`
    );
  };

  const handleShareWhatsApp = async (owner) => {
    if (!owner.owner_phone) {
      toast.error("Aucun numero de telephone enregistre pour ce proprietaire");
      return;
    }
    // Auto-download the PDF so the user can attach it
    await handleDownloadOwnerPdf(owner);
    // Sanitize phone: keep digits only, strip leading 0
    const digits = owner.owner_phone.replace(/\D/g, '');
    // If no country code, assume Mali (+223)
    const phone = digits.startsWith('223') ? digits : `223${digits.replace(/^0+/, '')}`;
    const text = encodeURIComponent(buildOwnerMessage(owner));
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank', 'noopener');
    toast.info("WhatsApp ouvert. N'oubliez pas de joindre le PDF telecharge.");
  };

  const handleShareEmail = async (owner) => {
    // Auto-download the PDF
    await handleDownloadOwnerPdf(owner);
    const subject = encodeURIComponent(`Bilan financier - ${owner.owner_name}`);
    const body = encodeURIComponent(buildOwnerMessage(owner));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    toast.info("Client mail ouvert. N'oubliez pas de joindre le PDF telecharge.");
  };


  const paymentMethodLabels = {
    'especes': 'Espèces',
    'virement': 'Virement bancaire',
    'cheque': 'Chèque',
    'mobile_money': 'Mobile Money'
  };

  return (
    <div className="space-y-6" data-testid="owner-balance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
            Bilan Propriétaires
          </h1>
          <p className="text-gray-500 mt-1">
            Consultez le solde disponible et effectuez des retraits pour les propriétaires
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={openGlobalBalance}
              className="bg-[#D4A745] hover:bg-[#B8902F] text-[#1B3A4B] font-semibold"
              data-testid="open-global-balance-btn"
            >
              <ListChecks className="w-4 h-4 mr-2" />
              Relevé global de tous les propriétaires
            </Button>
          </div>
        )}
      </div>

      {/* Selection Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Wallet className="w-5 h-5 text-[#1B3A4B]" />
            Sélection du Bien
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Bien immobilier en location
              </label>
              <Select
                value={selectedProperty}
                onValueChange={setSelectedProperty}
              >
                <SelectTrigger data-testid="property-select">
                  <SelectValue placeholder="Sélectionnez un bien" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((property) => (
                    <SelectItem key={property.id} value={property.id}>
                      {property.title} ({property.property_code}) - {property.owner_name || 'Propriétaire N/A'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end">
              <Button
                onClick={fetchBalance}
                disabled={!selectedProperty || loadingBalance}
                className="bg-[#1B3A4B] hover:bg-[#142D3A] w-full"
                data-testid="view-balance-btn"
              >
                {loadingBalance ? 'Chargement...' : 'Voir le Bilan'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Balance Results */}
      {balanceData && (
        <div className="space-y-6">
          {/* Owner Info */}
          <Card className="border-[#D4A373]/30 bg-[#D4A373]/5">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-[#D4A373]/20 rounded-full">
                    <User className="w-6 h-6 text-[#D4A373]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Propriétaire</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {balanceData.owner_name || 'Non renseigné'}
                    </p>
                    {balanceData.owner_phone && (
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {balanceData.owner_phone}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">{balanceData.property_code}</p>
                  <p className="font-medium text-gray-700">{balanceData.property_title}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="lg:col-span-5 kpi-premium rise-in" data-testid="payment-rate-card">
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl shrink-0 shadow-inner" style={{ background: 'linear-gradient(135deg, #ECFDF5 0%, #FEF3C7 100%)' }}>
                    <TrendingUp className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1B3A4B]">
                      Taux de paiement <span className="text-[11px] text-gray-400 font-normal">(basé sur les paiements de loyer confirmés)</span>
                    </p>
                    <div className="flex items-baseline gap-3 mt-2 flex-wrap">
                      <p className="text-4xl font-extrabold tabular-nums text-gradient-gold" style={{ lineHeight: 1 }} data-testid="payment-rate-percentage">
                        {balanceData.payment_rate_percentage ?? 0}%
                      </p>
                      <p className="text-sm text-gray-500 tabular-nums">
                        {formatCurrency(balanceData.total_received, 'XOF')} <span className="text-gray-300">/</span> {formatCurrency(balanceData.total_expected || 0, 'XOF')}
                      </p>
                    </div>
                    <div className="mt-3 h-2.5 bg-slate-100 rounded-full overflow-hidden relative">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${Math.min(balanceData.payment_rate_percentage ?? 0, 100)}%`,
                          background: 'linear-gradient(90deg, #10B981 0%, #D4A745 60%, #F2A900 100%)',
                        }}
                      />
                      <div className="absolute inset-0 progress-shimmer rounded-full" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Encaissé</p>
                    <p className="text-lg font-semibold text-green-600">
                      {formatCurrency(balanceData.total_received, 'XOF')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Percent className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Commission ({balanceData.commission_rate}%)</p>
                    <p className="text-lg font-semibold text-purple-600">
                      - {formatCurrency(balanceData.total_commission, 'XOF')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Receipt className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Net Propriétaire</p>
                    <p className="text-lg font-semibold text-blue-600">
                      {formatCurrency(balanceData.net_for_owner, 'XOF')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <TrendingDown className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Retiré</p>
                    <p className="text-lg font-semibold text-orange-600">
                      - {formatCurrency(balanceData.total_withdrawn, 'XOF')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className={balanceData.available_balance < 0 ? "bg-red-600 text-white" : "bg-[#1B3A4B] text-white"}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Wallet className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/70">{balanceData.available_balance < 0 ? "Découvert" : "Solde Disponible"}</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(balanceData.available_balance, 'XOF')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Action Button - Always visible for Admin */}
          {isAdmin && (
            <Card className="border-[#1B3A4B]/30">
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-[#1B3A4B]" />
                    <div>
                      <p className="font-medium text-gray-900">Effectuer un retrait</p>
                      <p className={`text-sm ${balanceData.available_balance < 0 ? "text-red-500 font-medium" : "text-gray-500"}`}>
                        Solde: {formatCurrency(balanceData.available_balance, 'XOF')}
                        {balanceData.available_balance < 0 && " (Découvert)"}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => setShowWithdrawModal(true)}
                    className="bg-[#D4A373] hover:bg-[#c49566] text-white"
                    data-testid="withdraw-btn"
                  >
                    <ArrowDownToLine className="w-4 h-4 mr-2" />
                    Effectuer un Retrait
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {!isAdmin && (
            <Card className="border-yellow-300 bg-yellow-50">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                  <p className="text-sm text-yellow-800">
                    Seuls les administrateurs peuvent effectuer des retraits. Contactez un administrateur si nécessaire.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Withdrawals History */}
          {balanceData.withdrawals && balanceData.withdrawals.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-[#D4A373]" />
                  Historique des Retraits
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Référence</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Date</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Bénéficiaire</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Mode</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600">Montant</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600">Solde Après</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {balanceData.withdrawals.map((withdrawal) => (
                        <tr key={withdrawal.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-mono text-sm text-[#1B3A4B]">
                            {withdrawal.reference}
                          </td>
                          <td className="p-3 text-sm text-gray-600">
                            {formatDate(withdrawal.created_at)}
                          </td>
                          <td className="p-3">
                            <p className="font-medium">{withdrawal.beneficiary_name}</p>
                            {withdrawal.beneficiary_phone && (
                              <p className="text-xs text-gray-500">{withdrawal.beneficiary_phone}</p>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <Badge variant="outline">
                              {paymentMethodLabels[withdrawal.payment_method] || withdrawal.payment_method}
                            </Badge>
                          </td>
                          <td className="p-3 text-right font-semibold text-orange-600">
                            - {formatCurrency(withdrawal.amount, 'XOF')}
                          </td>
                          <td className="p-3 text-right text-gray-600">
                            {formatCurrency(withdrawal.balance_after, 'XOF')}
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex justify-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadReceipt(withdrawal.id, withdrawal.reference)}
                                disabled={downloadingPdf === withdrawal.id}
                                className="border-[#1B3A4B] text-[#1B3A4B] hover:bg-[#1B3A4B]/10"
                                data-testid={`download-receipt-${withdrawal.id}`}
                              >
                                {downloadingPdf === withdrawal.id ? (
                                  '...'
                                ) : (
                                  <>
                                    <Download className="w-3 h-3 mr-1" />
                                    PDF
                                  </>
                                )}
                              </Button>
                              {isAdmin && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteWithdrawal(withdrawal.id, withdrawal.reference)}
                                  className="border-red-500 text-red-500 hover:bg-red-50"
                                  data-testid={`delete-withdrawal-${withdrawal.id}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* No withdrawals message */}
          {(!balanceData.withdrawals || balanceData.withdrawals.length === 0) && (
            <Card>
              <CardContent className="py-8 text-center">
                <History className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Aucun retrait effectué pour ce bien</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Initial State */}
      {!balanceData && !loadingBalance && (
        <Card>
          <CardContent className="py-16 text-center">
            <Wallet className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-500">Sélectionnez un bien immobilier</h3>
            <p className="text-gray-400 mt-2">
              Choisissez un bien en location pour consulter le bilan financier du propriétaire
            </p>
          </CardContent>
        </Card>
      )}

      {/* Withdrawal Modal */}
      <Dialog open={showWithdrawModal} onOpenChange={setShowWithdrawModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowDownToLine className="w-5 h-5 text-[#D4A373]" />
              Effectuer un Retrait
            </DialogTitle>
            <DialogDescription>
              Enregistrez un retrait pour le propriétaire du bien {balanceData?.property_title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Available balance reminder */}
            <div className={`p-3 rounded-lg ${(balanceData?.available_balance || 0) < 0 ? 'bg-red-50 border border-red-200' : 'bg-[#1B3A4B]/10'}`}>
              <p className="text-sm text-gray-600">
                Solde actuel: <span className={`font-bold ${(balanceData?.available_balance || 0) < 0 ? 'text-red-600' : 'text-[#1B3A4B]'}`}>{formatCurrency(balanceData?.available_balance || 0, 'XOF')}</span>
                {(balanceData?.available_balance || 0) < 0 && <span className="text-red-500 text-xs ml-1">(Découvert)</span>}
              </p>
            </div>

            {/* Amount */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Montant du retrait (XOF) *
              </label>
              <Input
                type="number"
                placeholder="Ex: 500000"
                value={withdrawalForm.amount}
                onChange={(e) => setWithdrawalForm({...withdrawalForm, amount: e.target.value})}
                data-testid="withdrawal-amount"
              />
            </div>

            {/* Beneficiary Name */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Nom du bénéficiaire *
              </label>
              <Input
                placeholder="Nom complet du bénéficiaire"
                value={withdrawalForm.beneficiary_name}
                onChange={(e) => setWithdrawalForm({...withdrawalForm, beneficiary_name: e.target.value})}
                data-testid="withdrawal-beneficiary"
              />
            </div>

            {/* Beneficiary Phone */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Téléphone du bénéficiaire
              </label>
              <Input
                placeholder="+223 XX XX XX XX"
                value={withdrawalForm.beneficiary_phone}
                onChange={(e) => setWithdrawalForm({...withdrawalForm, beneficiary_phone: e.target.value})}
                data-testid="withdrawal-phone"
              />
            </div>

            {/* Payment Method */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Mode de paiement
              </label>
              <Select
                value={withdrawalForm.payment_method}
                onValueChange={(value) => setWithdrawalForm({...withdrawalForm, payment_method: value})}
              >
                <SelectTrigger data-testid="withdrawal-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="especes">Espèces</SelectItem>
                  <SelectItem value="virement">Virement bancaire</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Observations (optionnel)
              </label>
              <Textarea
                placeholder="Notes ou commentaires..."
                value={withdrawalForm.notes}
                onChange={(e) => setWithdrawalForm({...withdrawalForm, notes: e.target.value})}
                rows={2}
                data-testid="withdrawal-notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowWithdrawModal(false)}
              disabled={withdrawing}
            >
              Annuler
            </Button>
            <Button
              onClick={handleWithdraw}
              disabled={withdrawing || !withdrawalForm.amount || !withdrawalForm.beneficiary_name}
              className="bg-[#D4A373] hover:bg-[#c49566]"
              data-testid="confirm-withdrawal-btn"
            >
              {withdrawing ? (
                'Traitement...'
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Confirmer le Retrait
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global Balance Modal */}
      <Dialog open={showGlobalModal} onOpenChange={setShowGlobalModal}>
        <DialogContent className="sm:max-w-[1100px] max-h-[92vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Users className="w-5 h-5 text-[#D4A745]" />
              Relevé global des soldes propriétaires
            </DialogTitle>
            <DialogDescription>
              Vue consolidée de tous les propriétaires de l&apos;agence avec leurs encaissements,
              commissions, retraits et soldes disponibles.
            </DialogDescription>
          </DialogHeader>

          {loadingGlobal ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-[#1B3A4B]" />
            </div>
          ) : globalData ? (
            <div className="flex-1 overflow-y-auto min-h-0 space-y-5">
              {/* Taux de paiement global */}
              <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="global-payment-rate-card">
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg shrink-0">
                      <TrendingUp className="w-4 h-4 text-emerald-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-600">
                        Taux de paiement global <span className="text-[10px] text-gray-400">(basé sur les paiements de loyer)</span>
                      </p>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <p className="text-2xl font-bold text-emerald-700 tabular-nums">
                          {globalData.totals.payment_rate_percentage ?? 0}%
                        </p>
                        <p className="text-xs text-gray-500 tabular-nums">
                          {formatCurrency(globalData.totals.total_received, 'XOF')} / {formatCurrency(globalData.totals.total_expected || 0, 'XOF')}
                        </p>
                      </div>
                      <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-amber-400 rounded-full transition-all"
                          style={{ width: `${Math.min(globalData.totals.payment_rate_percentage ?? 0, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Global totals */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { label: 'Propriétaires', value: globalData.totals.owner_count, icon: Users, color: 'text-[#1B3A4B]', bg: 'bg-gray-100' },
                  { label: 'Biens', value: globalData.totals.property_count, icon: Building2, color: 'text-blue-700', bg: 'bg-blue-50' },
                  { label: 'Encaissé', value: formatCurrency(globalData.totals.total_received, 'XOF'), icon: TrendingUp, color: 'text-emerald-700', bg: 'bg-emerald-50' },
                  { label: 'Commission', value: formatCurrency(globalData.totals.total_commission, 'XOF'), icon: Percent, color: 'text-amber-700', bg: 'bg-amber-50' },
                  { label: 'Retraits', value: formatCurrency(globalData.totals.total_withdrawn, 'XOF'), icon: TrendingDown, color: 'text-red-700', bg: 'bg-red-50' },
                  {
                    label: 'Solde disponible',
                    value: formatCurrency(globalData.totals.available_balance, 'XOF'),
                    icon: Wallet,
                    color: globalData.totals.available_balance >= 0 ? 'text-[#0F7B3F]' : 'text-red-700',
                    bg: globalData.totals.available_balance >= 0 ? 'bg-[#D4A745]/15' : 'bg-red-50',
                  },
                ].map((s, i) => (
                  <Card key={i} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center flex-shrink-0`}>
                          <s.icon className={`w-4 h-4 ${s.color}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{s.label}</p>
                          <p className={`text-sm font-bold ${s.color} truncate tabular-nums`} data-testid={`global-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                            {s.value}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Owners table */}
              {globalData.owners.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Users className="w-14 h-14 mx-auto mb-3 opacity-40" />
                  <p>Aucun bien en location enregistré pour le moment</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm" data-testid="global-owners-table">
                    <thead className="bg-[#1B3A4B] text-white">
                      <tr>
                        <th className="px-3 py-2 text-left">Propriétaire</th>
                        <th className="px-3 py-2 text-center">Biens</th>
                        <th className="px-3 py-2 text-right">Encaissé</th>
                        <th className="px-3 py-2 text-right">Commission</th>
                        <th className="px-3 py-2 text-right">Net dû</th>
                        <th className="px-3 py-2 text-right">Retiré</th>
                        <th className="px-3 py-2 text-right">Solde</th>
                        <th className="px-3 py-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {globalData.owners.map((o, idx) => (
                        <React.Fragment key={`${o.owner_name}-${idx}`}>
                          <tr className="border-b border-gray-200 bg-gray-50 hover:bg-[#D4A745]/8 transition-colors">
                            <td className="px-3 py-3">
                              <div className="font-semibold text-gray-900">{o.owner_name}</div>
                              {o.owner_phone && (
                                <div className="text-xs text-gray-500 flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {o.owner_phone}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-3 text-center font-semibold text-[#1B3A4B] tabular-nums">{o.property_count}</td>
                            <td className="px-3 py-3 text-right text-emerald-700 font-medium tabular-nums">{formatCurrency(o.total_received, 'XOF')}</td>
                            <td className="px-3 py-3 text-right text-amber-700 tabular-nums">{formatCurrency(o.total_commission, 'XOF')}</td>
                            <td className="px-3 py-3 text-right text-gray-700 tabular-nums">{formatCurrency(o.net_for_owner, 'XOF')}</td>
                            <td className="px-3 py-3 text-right text-red-700 tabular-nums">{formatCurrency(o.total_withdrawn, 'XOF')}</td>
                            <td className={`px-3 py-3 text-right font-bold tabular-nums ${o.available_balance >= 0 ? 'text-[#0F7B3F]' : 'text-red-700'}`}>
                              {formatCurrency(o.available_balance, 'XOF')}
                            </td>
                            <td className="px-2 py-2">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => handleDownloadOwnerPdf(o)}
                                  title="Telecharger le bilan PDF"
                                  className="w-8 h-8 rounded-lg bg-[#1B3A4B] hover:bg-[#142D3A] text-white flex items-center justify-center transition-colors"
                                  data-testid={`download-owner-${idx}`}
                                >
                                  <FileDown className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleShareWhatsApp(o)}
                                  disabled={!o.owner_phone}
                                  title={o.owner_phone ? "Partager par WhatsApp" : "Numero non renseigne"}
                                  className="w-8 h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
                                  data-testid={`whatsapp-owner-${idx}`}
                                >
                                  <MessageCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleShareEmail(o)}
                                  title="Partager par Email"
                                  className="w-8 h-8 rounded-lg bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-colors"
                                  data-testid={`email-owner-${idx}`}
                                >
                                  <Mail className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {/* Per-property breakdown */}
                          {o.properties.map((p) => (
                            <tr key={p.property_id} className="border-b border-gray-100 hover:bg-[#FBFAF6]">
                              <td className="px-3 py-2 pl-8">
                                <button
                                  onClick={() => handleSelectFromGlobal(p.property_id)}
                                  className="text-left group inline-flex items-center gap-1.5"
                                  data-testid={`select-property-${p.property_id}`}
                                >
                                  <Building2 className="w-3 h-3 text-gray-400" />
                                  <span className="text-xs text-gray-700 group-hover:text-[#1B3A4B] group-hover:underline">
                                    {p.property_title}
                                  </span>
                                  <span className="text-[10px] font-mono text-gray-400">{p.property_code}</span>
                                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 text-[#D4A745] transition-opacity" />
                                </button>
                              </td>
                              <td className="px-3 py-2 text-center text-xs text-gray-500">1</td>
                              <td className="px-3 py-2 text-right text-xs text-emerald-700 tabular-nums">{formatCurrency(p.total_received, 'XOF')}</td>
                              <td className="px-3 py-2 text-right text-xs text-amber-700 tabular-nums">
                                {formatCurrency(p.total_commission, 'XOF')}
                                <span className="text-[10px] text-gray-400 ml-1">({p.commission_rate}%)</span>
                              </td>
                              <td className="px-3 py-2 text-right text-xs text-gray-600 tabular-nums">{formatCurrency(p.net_for_owner, 'XOF')}</td>
                              <td className="px-3 py-2 text-right text-xs text-red-600 tabular-nums">{formatCurrency(p.total_withdrawn, 'XOF')}</td>
                              <td className={`px-3 py-2 text-right text-xs font-semibold tabular-nums ${p.available_balance >= 0 ? 'text-[#0F7B3F]' : 'text-red-700'}`}>
                                {formatCurrency(p.available_balance, 'XOF')}
                              </td>
                              <td className="px-2 py-2"></td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-100 border-t-2 border-[#1B3A4B]">
                      <tr>
                        <td className="px-3 py-3 font-bold text-[#1B3A4B]">TOTAL GENERAL</td>
                        <td className="px-3 py-3 text-center font-bold tabular-nums">{globalData.totals.property_count}</td>
                        <td className="px-3 py-3 text-right font-bold text-emerald-700 tabular-nums">{formatCurrency(globalData.totals.total_received, 'XOF')}</td>
                        <td className="px-3 py-3 text-right font-bold text-amber-700 tabular-nums">{formatCurrency(globalData.totals.total_commission, 'XOF')}</td>
                        <td className="px-3 py-3 text-right font-bold text-gray-700 tabular-nums">{formatCurrency(globalData.totals.net_for_owner, 'XOF')}</td>
                        <td className="px-3 py-3 text-right font-bold text-red-700 tabular-nums">{formatCurrency(globalData.totals.total_withdrawn, 'XOF')}</td>
                        <td className={`px-3 py-3 text-right font-bold tabular-nums ${globalData.totals.available_balance >= 0 ? 'text-[#0F7B3F]' : 'text-red-700'}`}>
                          {formatCurrency(globalData.totals.available_balance, 'XOF')}
                        </td>
                        <td className="px-2 py-3"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          ) : null}

          <div className="pt-3 border-t flex flex-wrap justify-between items-center gap-2 flex-shrink-0">
            <p className="text-xs text-gray-500">
              {globalData?.generated_at && `Edité le ${formatDate(globalData.generated_at)}`}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowGlobalModal(false)}>
                Fermer
              </Button>
              <Button
                onClick={handleDownloadGlobalPdf}
                disabled={downloadingGlobalPdf || !globalData}
                className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                data-testid="download-global-pdf-btn"
              >
                {downloadingGlobalPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Télécharger en PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OwnerBalance;
